import { fetchData, patchData, postData } from 'modules/Axios';
import { showToast } from 'modules/Notifications';
import { isNotNilOrEmpty, isFalsy } from 'lib/Helpers';

import { isSegmentNameValid } from 'lib/Segments';

import mapUiSegmentProperties from '../utils/index';

export const defaultState = {
  isNameUnique: true,
  loading: false,
  locations: [],
  currentLocationPage: 0,
  locationSearchValue: '',
  totalLocationRecords: Number.MAX_SAFE_INTEGER,
  dayParts: [],
  ethnicities: [],
  orderingChannels: [],
  nameValid: true,
  segmentData: {
    name: '',
    description: '',
    locations: [],
    includeWithoutLocation: false,
    filters: [],
    profileSource: 'ALL',
  },
};

export const CREATE_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS = 'CREATE_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS';

export const receiveBrandOrderingChannels = orderingChannels => {
  return {
    type: CREATE_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS,
    payload: orderingChannels,
  };
};

function uniqueBy(arr, prop) {
  return arr.reduce((a, d) => {
    if (!a.includes(d[prop])) { a.push(d[prop]); }
    return a;
  }, []);
}

export const fetchBrandOrderingChannels = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/ordering-channel`))
      .catch(e => console.log('Could not fetch brand ethnicity: ', e))) || [];

    const channelsWithOtherOptionExcluded = data.filter(c => c.name.toLowerCase() !== 'other');

    const distinctOrderingChannels = uniqueBy(channelsWithOtherOptionExcluded, 'name');
    const hasOther = distinctOrderingChannels.find(x => x.toLowerCase() === 'other');
    if (!hasOther) {
      distinctOrderingChannels.push('Other');
    }
    const result = distinctOrderingChannels.map(o => ({ text: o, value: o }));
    return dispatch(receiveBrandOrderingChannels(result));
  };
};

export const UPDATE_CREATE_SEGMENT_DATA = 'UPDATE_CREATE_SEGMENT_DATA';
/**
 * Central Change Handler for All Sections of Segment Data
 * @param {string} field
 * @param {*} payload
 * @return {Function} dispatch of UPDATE_SEGMENT_DATA Payload
 */
export const updateSegmentData = (field, payload) => {
  return (dispatch, getState) => {
    const { nameValid, segmentData } = getState().createSegment;

    const data = { ...segmentData, [field]: payload };
    return dispatch({
      type: UPDATE_CREATE_SEGMENT_DATA,
      payload: {
        segmentData: data,
        nameValid: field === 'name'
          ? isSegmentNameValid(payload)
          : nameValid,
      },
    });
  };
};

export const CREATE_LOADING = 'CREATE_LOADING';
/**
 * Toggles Global Loading State for this Section
 * @param {boolean} bool
 * @return {Object} CREATE_LOADING Payload
 */
export const createLoading = bool => {
  return {
    type: CREATE_LOADING,
    payload: bool
  };
};

export const RECEIVE_LOCATIONS_CREATE_SEGMENT = 'RECEIVE_LOCATIONS_CREATE_SEGMENT';
/**
 * Receiver to Update Locations State
 * @param {Array.Object} locations
 * @return {Object} RECEIVE_LOCATIONS Payload
 */
export const receiveLocations = locations => {
  return {
    type: RECEIVE_LOCATIONS_CREATE_SEGMENT,
    payload: locations,
  };
};

export const updateLocationSearchValue = (searchValue) => {
  return async (dispatch, getState) => {
    await Promise.all([
      dispatch(updateLocationsPage(0)),
      dispatch(updateCreateSegmentLocationSearchValue(searchValue)),
    ]).then(() => {
      dispatch(fetchLocations(true));
    });
  };
};

export const UPDATE_CREATE_SEGMENT_LOCATION_SEARCH_VALUE = 'UPDATE_CREATE_SEGMENT_LOCATION_SEARCH_VALUE';
export const updateCreateSegmentLocationSearchValue = searchValue => {
  return {
    type: UPDATE_CREATE_SEGMENT_LOCATION_SEARCH_VALUE,
    payload: searchValue,
  };
};

export const fetchLocations = (shouldUpdateGrid = false) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const { currentLocationPage, locations, locationSearchValue } = getState().createSegment;
    const url = `/brands/${brand.id}/sites?size=20&page=${currentLocationPage}&sort=city,ASC`;
    try {
      const locationsData = locationSearchValue
        ? await dispatch(postData(url, { filter: {
          name: locationSearchValue,
          store_id: locationSearchValue,
          city: locationSearchValue,
          state: locationSearchValue,
          postal_code: locationSearchValue
        } }))
        : await dispatch(fetchData(url));

      if (currentLocationPage > locationsData.total_pages) {
        return;
      }
      dispatch(updateLocationsPage(currentLocationPage + 1));
      dispatch(receiveLocations({
        ...locationsData,
        locations: shouldUpdateGrid ? locationsData.content : locations.concat(locationsData.content),
      }));
    } catch (e) {
      console.log('Could not fetch brand locations: ', e);
    }
  };
};

export const UPDATE_LOCATIONS_PAGE = 'UPDATE_LOCATIONS_PAGE';
export const updateLocationsPage = page => ({
  type: UPDATE_LOCATIONS_PAGE,
  payload: page,
});

export const RECEIVE_BRAND_DATE_PARTS = 'RECEIVE_BRAND_DATE_PARTS';
/**
 * Receiver to Update Day Parts State
 * @param {Array.Object} day parts
 * @return {Object} RECEIVE_BRAND_DATE_PARTS Payload
 */
export const receiveBrandDateParts = dayParts => {
  return {
    type: RECEIVE_BRAND_DATE_PARTS,
    payload: dayParts,
  };
};

/**
 * Fetches Brand Day Parts
 * @return {Function} dispatch(receiveBrandDateParts())
 */
export const fetchBrandDayParts = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/day-parts`))
      .catch(e => console.log('Could not fetch brand day parts: ', e))) || [];
    const dayParts = data.sort((a, b) => (a.id - b.id)).map(o => ({ text: o.name, value: o.id }));
    return dispatch(receiveBrandDateParts(dayParts));
  };
};

export const RECEIVE_BRAND_ETHNICITIES = 'RECEIVE_BRAND_ETHNICITIES';
/**
 * Receiver to Update Ethnicities State
 * @param {Array.Object} Ethnicities
 * @return {Object} RECEIVE_BRAND_ETHNICITIES Payload
 */
export const receiveBrandEthnicities = ethnicities => {
  return {
    type: RECEIVE_BRAND_ETHNICITIES,
    payload: ethnicities,
  };
};

/**
 * Fetches Brand Ethnicities
 * @return {Function} dispatch(receiveBrandEthnicities())
 */
export const fetchBrandEthnicities = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/ethnicity`))
      .catch(e => console.log('Could not fetch brand ethnicity: ', e))) || [];
    const ethnicities = data.map(o => ({ text: o.name, value: o.id }));
    return dispatch(receiveBrandEthnicities(ethnicities));
  };
};

export const RECEIVE_SEGMENT = 'RECEIVE_SEGMENT';
/**
 * Receiver to Update SegmentData State
 * @param {Object} segment
 * @return {Object} RECEIVE_SEGMENT Payload
 */
export const receiveSegment = segment => {
  return {
    type: RECEIVE_SEGMENT,
    payload: segment,
  };
};

export const INIT_CREATE_SEGMENT = 'INIT_CREATE_SEGMENT';
/**
 * Initializes Create Segment Flow
 * @param {Object} Filters
 * @return {Function} Dispatch fetchLocations
 */
export const initCreateSegment = () => {
  return async dispatch => {
    dispatch(createLoading(true));
    await Promise.all([
      dispatch(updateLocationsPage(0)),
      dispatch(fetchLocations()),
      dispatch(fetchBrandDayParts()),
      dispatch(fetchBrandEthnicities()),
      dispatch(fetchBrandOrderingChannels()),
    ]).catch(e => {
      console.log('Promise.all in initCreateSegment failed...', e);
      dispatch(createLoading(false));
      return null;
    });
    return dispatch(createLoading(false));
  };
};

/**
 * Save a segments data in the database
 * @param {Function} cb - Redirect to `/segments`
 * @param {boolean} redirect - Flag for whether or not to call cb()
 * @return {(Function|Object)} Redirect or return segment data
 */
export const saveSegment = (cb, redirect = true) => {
  return async (dispatch, getState) => {
    dispatch(createLoading(true));
    const { brand } = getState().app;
    const { segmentData } = getState().createSegment;
    const data = mapUiSegmentProperties(segmentData);
    console.log(data);
    const segment = await dispatch(postData(`/brands/${brand.id}/segments`, data))
      .catch(e => {});
    if (isFalsy(segment)) {
      dispatch(showToast({
        state: 'ERROR',
        title: 'Error',
        message: 'Issue saving this segment. Please try again later',
        dismissible: true,
        dismissAfter: 1500,
      }));
      return dispatch(createLoading(false));
    }
    if (redirect) {
      dispatch(createLoading(false));
      dispatch(showToast({
        state: 'SUCCESS',
        title: 'Success!',
        message: 'Segment saved',
        dismissible: true,
        dismissAfter: 3000,
      }));
      return cb();
    }
    return segment;
  };
};

/**
 * Submits a segment for processing by hitting `/activate`
 * @param {Function} cb - Redirect to `/segments`
 * @return {Function} cb()
 */
export const activateSegment = cb => {
  return async (dispatch, getState) => {
    dispatch(createLoading(true));
    const { brand } = getState().app;

    const segment = await dispatch(saveSegment(cb, false));
    if (isFalsy(segment)) return dispatch(createLoading(false));

    const result = await dispatch(patchData(`/brands/${brand.id}/segments/${segment.id}/activate`, {}))
      .catch(e => {
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue submitting this segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        dispatch(createLoading(false));
        return null;
      });
    /* Submit attempt failed, return null and keep user on the page. */
    if (isFalsy(result)) return null;

    dispatch(createLoading(false));
    dispatch(showToast({
      state: 'SUCCESS',
      title: 'Success!',
      message: 'Segment saved & submitted for processing',
      dismissible: true,
      dismissAfter: 3000,
    }));
    return cb();
  };
};

export const CREATE_SEGMENT_CHECK_NAME_UNIQUENESS = 'CREATE_SEGMENT_CHECK_NAME_UNIQUENESS';
/**
 * Uses `isUnique` Endpoint to Verify if Given Segment Name is Unique
 * @return {boolean}
 */
export const checkNameUniqueness = () => {
  return async (dispatch, getState) => {
    const { segmentData: { name } } = getState().createSegment;
    return dispatch(checkSegmentNameUniqueness(name));
  };
};

export const CHECK_SEGMENT_NAME_UNIQUENESS = 'CHECK_SEGMENT_NAME_UNIQUENESS';
/**
 * Checks if given name is unique across both legacy and explorer segments.
 * @return {boolean}
 */
export const checkSegmentNameUniqueness = (name) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const legacySegmentUrl = `/brands/${brand.id}/segments/names/isUnique`;
    const explorerSegmentUrl = `/v2/brands/${brand.id}/segments?size=10000`;

    // Skip uniqueness check if name is empty and just return true, as there is
    // other validation for empty name.
    if (!name) {
      return dispatch({
        type: CHECK_SEGMENT_NAME_UNIQUENESS,
        payload: true,
      });
    }

    // Need to check if unique across both legacy and explorer segments. Call
    // for explorer segments can be removed after legacy check is updated to
    // also check explorer segments (MKC-4723).
    const [legacySegmentsCheck, explorerSegmentsCheck] = await Promise.all([
      dispatch(postData(legacySegmentUrl, { name })),
      dispatch(fetchData(explorerSegmentUrl, { name })),
    ]).catch(e => {
      console.error('Issue checking segment name uniqueness:', e);
      return [{}, {}];
    });
    const isUnique = !!(
      legacySegmentsCheck?.isUnique
      && !explorerSegmentsCheck?.content?.some((segment) => segment?.name === name)
    );

    return dispatch({
      type: CHECK_SEGMENT_NAME_UNIQUENESS,
      payload: isUnique,
    });
  };
};

export const RESET_SEGMENT_ROUTE = 'RESET_SEGMENT_ROUTE';
/**
 * Resets Segment State Tree Branch
 * @return {Object} RESET_SEGMENT_ROUTE Payload
 */
export const resetSegmentRoute = () => {
  return {
    type: RESET_SEGMENT_ROUTE,
  };
};

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case CREATE_LOADING:
      return { ...state, loading: action.payload };
    case CREATE_SEGMENT_CHECK_NAME_UNIQUENESS:
    case CHECK_SEGMENT_NAME_UNIQUENESS:
      return { ...state, isNameUnique: action.payload };
    case CREATE_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS:
      return { ...state, orderingChannels: action.payload };
    case UPDATE_LOCATIONS_PAGE:
      return { ...state, currentLocationPage: action.payload };
    case RECEIVE_LOCATIONS_CREATE_SEGMENT:
      return {
        ...state,
        locations: action.payload.locations,
        totalLocationRecords: isNotNilOrEmpty(action.payload.total_elements) ? action.payload.total_elements : state.totalRecords, };
    case UPDATE_CREATE_SEGMENT_LOCATION_SEARCH_VALUE:
      return {
        ...state,
        locationSearchValue: action.payload,
      };
    case RECEIVE_BRAND_DATE_PARTS:
      return { ...state, dayParts: action.payload };
    case RECEIVE_BRAND_ETHNICITIES:
      return { ...state, ethnicities: action.payload };
    case RECEIVE_SEGMENT:
      return { ...state, segmentData: action.payload, loading: false };
    case RESET_SEGMENT_ROUTE:
      return { ...defaultState };
    case UPDATE_CREATE_SEGMENT_DATA:
      return {
        ...state,
        nameValid: action.payload.nameValid,
        segmentData: action.payload.segmentData,
      };
    default:
      return state;
  }
}
